/**
 * Lightweight, privacy-friendly behaviour tracking used to enrich CRM leads.
 * Everything is kept in sessionStorage (no cookies, no third party) and is
 * attached to the lead notes when a form is submitted.
 */

const KEY = "tc_behavior";

interface BehaviorData {
  /** Pages visited in this session, in order, with seconds spent on each. */
  pages: { path: string; seconds: number }[];
  /** Max scroll depth per page (percentage). */
  scroll: Record<string, number>;
  /** Interactions worth reporting (WhatsApp clicks, photo opens, etc). */
  events: string[];
  /** Timestamp of the session start. */
  startedAt: number;
}

const empty = (): BehaviorData => ({ pages: [], scroll: {}, events: [], startedAt: Date.now() });

function read(): BehaviorData {
  if (typeof window === "undefined") return empty();
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return empty();
    return { ...empty(), ...(JSON.parse(raw) as BehaviorData) };
  } catch {
    return empty();
  }
}

function write(data: BehaviorData) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/** Records a custom interaction (e.g. "Clicou WhatsApp"). */
export function trackEvent(label: string) {
  if (typeof window === "undefined") return;
  const data = read();
  const entry = `${label} (${new Date().toLocaleTimeString("pt-BR")})`;
  if (!data.events.includes(entry)) data.events.push(entry.slice(0, 120));
  data.events = data.events.slice(-25);
  write(data);
}

/**
 * Starts tracking time and scroll depth for the current page.
 * Returns a cleanup function that persists the elapsed time.
 */
export function trackPage(path: string): () => void {
  if (typeof window === "undefined") return () => {};

  const enteredAt = Date.now();
  let maxScroll = 0;

  const onScroll = () => {
    const doc = document.documentElement;
    const total = doc.scrollHeight - window.innerHeight;
    const pct = total > 0 ? Math.round((window.scrollY / total) * 100) : 100;
    if (pct > maxScroll) maxScroll = Math.min(100, pct);
  };

  const persist = () => {
    const seconds = Math.round((Date.now() - enteredAt) / 1000);
    const data = read();
    const existing = data.pages.find((p) => p.path === path);
    if (existing) existing.seconds += seconds;
    else data.pages.push({ path, seconds });
    data.pages = data.pages.slice(-25);
    data.scroll[path] = Math.max(data.scroll[path] || 0, maxScroll);
    write(data);
  };

  const onClick = (e: MouseEvent) => {
    const el = (e.target as HTMLElement | null)?.closest("a,button") as HTMLElement | null;
    if (!el) return;
    const href = el.getAttribute("href") || "";
    const label = (el.textContent || "").trim().slice(0, 60);
    if (href.includes("wa.me") || /whats/i.test(label)) trackEvent(`Clicou WhatsApp: ${label || href}`);
    else if (href.startsWith("tel:")) trackEvent(`Clicou telefone: ${href}`);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("click", onClick);
  window.addEventListener("pagehide", persist);
  onScroll();

  return () => {
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("click", onClick);
    window.removeEventListener("pagehide", persist);
    persist();
  };
}

/** Human readable summary (pt-BR) appended to the lead notes. */
export function behaviorSummary(currentPath?: string): string {
  if (typeof window === "undefined") return "";
  const data = read();

  const pages = [...data.pages];
  if (currentPath) {
    const now = Math.round((Date.now() - data.startedAt) / 1000);
    if (!pages.some((p) => p.path === currentPath)) pages.push({ path: currentPath, seconds: now });
  }

  const totalSeconds = Math.round((Date.now() - data.startedAt) / 1000);
  const fmt = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m${s % 60}s` : `${s}s`);

  const parts = [
    `Tempo total na sessão: ${fmt(totalSeconds)}`,
    pages.length > 0 &&
      `Páginas vistas: ${pages
        .map((p) => `${p.path} (${fmt(p.seconds)}${data.scroll[p.path] ? `, rolagem ${data.scroll[p.path]}%` : ""})`)
        .join(" > ")}`,
    data.events.length > 0 && `Interações: ${data.events.join(", ")}`,
  ].filter(Boolean);

  return parts.join(" | ");
}
